"use client";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "next/navigation";
import { useEffect, useMemo } from "react";
import { EventPageClient } from "@/features/hiives/components/event-detail-hub/event-page";
import { hiivesKeys } from "@/features/hiives/queries/keys";
import { EVENT_DETAIL_LOAD_ERROR_MESSAGE } from "./event-detail-section.shared";
import type { EventDetailSectionData } from "./event-detail-section.types";

type EventDetailSectionClientProps = {
  initialData: EventDetailSectionData;
};

export function EventDetailSectionClient({
  initialData,
}: EventDetailSectionClientProps) {
  const params = useParams<{ hiiveSlug: string; eventSlug: string }>();
  const hiiveSlug = params.hiiveSlug ?? "";
  const eventSlug = params.eventSlug ?? "";
  const hasSlugs = hiiveSlug.length > 0 && eventSlug.length > 0;
  const queryClient = useQueryClient();
  const queryKey = useMemo(
    () =>
      hasSlugs
        ? hiivesKeys.eventDetailPage(hiiveSlug, eventSlug)
        : ([...hiivesKeys.eventAll(), "detail", "invalid", "invalid"] as const),
    [hasSlugs, hiiveSlug, eventSlug],
  );

  const eventDetailQuery = useQuery<EventDetailSectionData>({
    queryKey,
    queryFn: async () => initialData,
    initialData,
    staleTime: Number.POSITIVE_INFINITY,
    enabled: hasSlugs,
  });

  // Keep the cache on the payload the server last rendered.
  //
  // This query never refetches — its own queryFn just returns the prop, and
  // `initialData` alone only seeds an empty cache. So when something asks the
  // server for a fresh answer with `router.refresh()` (a crossed booking
  // window, say), the new payload has to be written in for the page to see it.
  // The server's `HydrationBoundary` should also carry it, but the booking CTA
  // depends on this landing, so do not leave it to that alone.
  useEffect(() => {
    queryClient.setQueryData(queryKey, initialData);
  }, [queryClient, queryKey, initialData]);

  const eventDetail = eventDetailQuery.data ?? initialData;

  if (!hasSlugs || !eventDetail || eventDetailQuery.error) {
    return (
      <div className="flex h-dvh flex-col items-center justify-center bg-background px-4 text-center">
        <p className="text-sm text-text-secondary">
          {EVENT_DETAIL_LOAD_ERROR_MESSAGE}
        </p>
      </div>
    );
  }

  return (
    <EventPageClient
      hiive={eventDetail.hiive}
      event={eventDetail.event}
      occurrences={eventDetail.occurrences}
      bookingRestriction={eventDetail.bookingRestriction}
      bookingWindow={eventDetail.bookingWindow}
      canBookEvent={eventDetail.canBookEvent}
      waitlistOnly={eventDetail.waitlistOnly}
      bookingAvailabilityBlocked={eventDetail.bookingAvailabilityBlocked}
      canStartGuestBooking={eventDetail.canStartGuestBooking}
      canViewAttendeeList={eventDetail.canViewAttendeeList}
      canViewAttendeeCount={eventDetail.canViewAttendeeCount}
      membershipStatus={eventDetail.membershipStatus}
      meetsTierRequirement={eventDetail.meetsTierRequirement}
      hiiveSlug={eventDetail.hiiveSlug}
      eventSlug={eventDetail.eventSlug}
      userId={eventDetail.userId}
      viewer={
        eventDetail.sessionUser
          ? {
              id: eventDetail.sessionUser.id,
              displayName: eventDetail.sessionUser.name,
              username: eventDetail.sessionUser.username,
              avatarUrl: eventDetail.sessionUser.image,
            }
          : undefined
      }
      hostUserIds={eventDetail.hostUserIds}
      viewerAlbumTag={eventDetail.viewerAlbumTag}
      nextOccurrence={eventDetail.nextOccurrence}
      allowedTierNames={eventDetail.allowedTierNames}
      eligibilityGate={eventDetail.eligibilityGate}
      pendingTierRequestId={eventDetail.pendingTierRequestId ?? null}
      distanceKm={eventDetail.distanceKm}
      hiiveMemberCount={eventDetail.hiiveMemberCount}
      hiiveEventCount={eventDetail.hiiveEventCount}
      packs={eventDetail.packs}
      packStripContext={eventDetail.packStripContext}
      addons={eventDetail.addons}
      viewerPackHolding={eventDetail.viewerPackHolding}
      storyRingStatus={eventDetail.storyRingStatus}
      tabCounts={eventDetail.tabCounts}
    />
  );
}
